/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { API_PATHS, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';
import { makeRequestWithResponse } from './shared';
import * as types from './types/non-financial-drivers';
import { DriverNodeList } from '@futrli-sdk/predict';

/**
 * Saves a new formula driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param data - the new formula driver data to save
 * @throws error
 */
export const createFormulaDriver = async (
  workspaceId: string,
  organisationId: string,
  data: types.FormulaNonFinancialDriverSaveData
): Promise<types.FormulaNonFinancialDriver> => {
  const path = interpolatePathParams(API_PATHS.FORMULA_NON_FINANCIAL_DRIVER, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.FormulaNonFinancialDriver>({
    url: path,
    method: REQUEST_METHODS.POST,
    data
  });
};

/**
 * Saves a new freestyle driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param data - the new freestyle driver data to save
 * @throws error
 */
export const createFreestyleDriver = async (
  workspaceId: string,
  organisationId: string,
  data: types.FreestyleNonFinancialDriverSaveData
): Promise<types.FreestyleNonFinancialDriver> => {
  const path = interpolatePathParams(API_PATHS.FREESTYLE_NON_FINANCIAL_DRIVER, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.FreestyleNonFinancialDriver>({
    url: path,
    method: REQUEST_METHODS.POST,
    data
  });
};

/**
 * Creates a new driver group (and adds the given nodes to it)
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param data - the new group data to save
 * @throws error
 */
export const createGroup = async (
  workspaceId: string,
  organisationId: string,
  data: types.NonFinancialDriverGroupSaveData
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.NON_FINANCIAL_DRIVER_GROUP, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.POST,
    data
  });
};

/**
 * Duplicate a driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param driverId - id of the driver to duplicate
 * @param data - optional data to pass to the duplication endpoint
 * @throws error
 */
export const duplicateDriver = async (
  workspaceId: string,
  organisationId: string,
  driverId: string,
  data: types.NonFinancialDriverDuplicateData
): Promise<{ data: types.FreestyleNonFinancialDriver | types.FormulaNonFinancialDriver }> => {
  const path = interpolatePathParams(API_PATHS.DUPLICATE_NON_FINANCIAL_DRIVER, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.DRIVER_NODE_ID]: driverId
  });

  return makeRequestWithResponse<{ data: types.FreestyleNonFinancialDriver | types.FormulaNonFinancialDriver }>({
    url: path,
    method: REQUEST_METHODS.POST,
    data
  });
};

/**
 * Gets the non financial drivers list
 *
 * @param {string} workspaceId - workspace to get the organisation from
 * @param {string} organisationId - organisation that the drivers are related to
 * @throws error
 */
export const readDriversData = async (
  workspaceId: string,
  organisationId: string
): Promise<{ data: types.NonFinancialDriverValues[] }> => {
  const path = interpolatePathParams(API_PATHS.NON_FINANCIAL_DRIVER_DATA, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<{ data: types.NonFinancialDriverValues[] }>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Gets the non financial driver groups / ordering data
 *
 * @param {string} workspaceId - workspace to get the organisation from
 * @param {string} organisationId - organisation that the drivers are related to
 * @throws error
 */
export const readDriversOrder = async (
  workspaceId: string,
  organisationId: string
): Promise<types.NonFinancialDriverNodeList[]> => {
  const path = interpolatePathParams(API_PATHS.NON_FINANCIAL_DRIVER_NODE, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.NonFinancialDriverNodeList[]>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Fetch data for a specific formula driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param data - the new formula driver data to save
 * @throws error
 */
export const readFormulaDriver = async (
  workspaceId: string,
  organisationId: string,
  driverId: string
): Promise<types.FormulaNonFinancialDriver> => {
  const path = interpolatePathParams(API_PATHS.FORMULA_NON_FINANCIAL_DRIVER_BY_ID, {
    [ROUTE_PARAMS.DRIVER_NODE_ID]: driverId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.FormulaNonFinancialDriver>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Gets the data used to build the list of formula options (@ menu) in the formula editor
 *
 * @param {string} workspaceId - workspace to get the organisation from
 * @param {string} organisationId - organisation that the drivers are related to
 * @throws error
 */
export const readFormulaOptions = async (
  workspaceId: string,
  organisationId: string
): Promise<types.NonFinancialDriverFormulaOptionsResponse> => {
  const path = interpolatePathParams(API_PATHS.FORMULA_NON_FINANCIAL_DRIVER_OPTIONS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.NonFinancialDriverFormulaOptionsResponse>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Fetch data for a specific freestyle driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param data - the new freestyle driver data to save
 * @throws error
 */
export const readFreestyleDriver = async (
  workspaceId: string,
  organisationId: string,
  driverId: string
): Promise<types.FreestyleNonFinancialDriver> => {
  const path = interpolatePathParams(API_PATHS.FREESTYLE_NON_FINANCIAL_DRIVER_BY_ID, {
    [ROUTE_PARAMS.DRIVER_NODE_ID]: driverId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.FreestyleNonFinancialDriver>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Updates the ordering of the drivers
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param data - the driver order data to save
 * @throws error
 */
export const updateDriverOrdering = async (
  workspaceId: string,
  organisationId: string,
  data: DriverNodeList[]
): Promise<DriverNodeList[]> => {
  const path = interpolatePathParams(API_PATHS.REORDER_NON_FINANCIAL_DRIVER, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<DriverNodeList[]>({
    url: path,
    method: REQUEST_METHODS.PUT,
    data
  });
};

/**
 * Updates an exiting freestyle driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param driverId - the id of the freestyle driver to update
 * @param data - the freestyle driver data to update
 * @throws error
 */
export const updateFreestyleDriver = async (
  workspaceId: string,
  organisationId: string,
  driverId: string,
  data: types.FreestyleNonFinancialDriverUpdateData
): Promise<types.FreestyleNonFinancialDriver> => {
  const path = interpolatePathParams(API_PATHS.FREESTYLE_NON_FINANCIAL_DRIVER_BY_ID, {
    [ROUTE_PARAMS.DRIVER_NODE_ID]: driverId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.FreestyleNonFinancialDriver>({
    url: path,
    method: REQUEST_METHODS.PATCH,
    data
  });
};

/**
 * Updates an exiting formula driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param driverId - the id of the formula driver to update
 * @param data - the formula driver data to update
 * @throws error
 */
export const updateFormulaDriver = async (
  workspaceId: string,
  organisationId: string,
  driverId: string,
  data: types.FormulaNonFinancialDriverUpdateData
): Promise<types.FormulaNonFinancialDriver> => {
  const path = interpolatePathParams(API_PATHS.FORMULA_NON_FINANCIAL_DRIVER_BY_ID, {
    [ROUTE_PARAMS.DRIVER_NODE_ID]: driverId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<types.FormulaNonFinancialDriver>({
    url: path,
    method: REQUEST_METHODS.PATCH,
    data
  });
};

/**
 * Updates a driver group
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param groupId - the id of the group to update
 * @param data - the new group data to save
 * @throws error
 */
export const updateGroup = async (
  workspaceId: string,
  organisationId: string,
  groupId: string,
  data: types.NonFinancialDriverGroupUpdateData
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.NON_FINANCIAL_DRIVER_GROUP_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.DRIVER_NODE_ID]: groupId
  });

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.PUT,
    data
  });
};

/**
 * Deletes a driver
 *
 * @param workspaceId - workspace to get the organisation from
 * @param organisationId - organisation that the drivers are related to
 * @param driverId - the id of the driver to delete
 * @throws error
 */
export const deleteDriver = async (workspaceId: string, organisationId: string, driverId: string): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.NON_FINANCIAL_DRIVER_NODE_BY_ID, {
    [ROUTE_PARAMS.DRIVER_NODE_ID]: driverId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.DELETE
  });
};
